import { useEffect, useState } from 'react';
import { FaCheckCircle, FaUserPlus } from 'react-icons/fa';
import { Badge } from '../../../components/Badge';
import { cn } from '../../../utils/cn';
import { usePasStore } from '../pasStore';
import { Button } from '../components/Button';
import { Card, PageHeader, SectionCard } from '../components/primitives';

type AdminTab = 'users' | 'distribution' | 'configuration';

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; description: string }> = ({
  checked,
  onChange,
  label,
  description,
}) => (
  <div className="flex items-center justify-between gap-4 py-3">
    <div className="min-w-0">
      <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{label}</p>
      <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn('relative h-6 w-11 flex-shrink-0 rounded-full transition-colors')}
      style={{ backgroundColor: checked ? 'var(--color-primary-500)' : 'var(--color-border-medium)' }}
    >
      <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform', checked ? 'translate-x-[22px]' : 'translate-x-0.5')} />
    </button>
  </div>
);

export const AdministrationPage: React.FC = () => {
  const [tab, setTab] = useState<AdminTab>('users');
  const pushToast = usePasStore((s) => s.pushToast);
  const users = usePasStore((s) => s.users);
  const brokers = usePasStore((s) => s.brokers);
  const carriers = usePasStore((s) => s.carriers);
  const products = usePasStore((s) => s.products);
  const workflowSettings = usePasStore((s) => s.workflowSettings);

  const [rules, setRules] = useState({
    requireBinder: true,
    autoAssign: true,
    dualApproval: false,
    notifyBroker: true,
  });
  const [activeProducts, setActiveProducts] = useState<Record<string, boolean>>({});

  // Seed workflow rules from the backend once they load.
  useEffect(() => {
    if (workflowSettings) {
      setRules({
        requireBinder: workflowSettings.requireBinder,
        autoAssign: workflowSettings.autoAssign,
        dualApproval: workflowSettings.dualApproval,
        notifyBroker: workflowSettings.notifyBroker,
      });
    }
  }, [workflowSettings]);

  // Seed product toggles from the product list (all on unless the backend says otherwise).
  useEffect(() => {
    const active = workflowSettings?.activeProducts;
    setActiveProducts(
      Object.fromEntries(products.map((p) => [p, active ? active.includes(p) : true])),
    );
  }, [products, workflowSettings]);

  const tabs: { key: AdminTab; label: string }[] = [
    { key: 'users', label: 'Users & Roles' },
    { key: 'distribution', label: 'Brokers & Carriers' },
    { key: 'configuration', label: 'Configuration' },
  ];

  return (
    <div>
      <PageHeader
        title="Administration"
        subtitle="Manage users, distribution partners, products, and workflow rules."
      />

      <div className="mb-6 flex gap-1 overflow-x-auto border-b" style={{ borderColor: 'var(--color-border-light)' }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={cn('whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors', tab === t.key ? 'border-current' : 'border-transparent hover:opacity-80')}
            style={{ color: tab === t.key ? 'var(--color-primary-600)' : 'var(--color-text-secondary)' }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'users' && (
        <SectionCard
          title="Users & roles"
          description="People with access to the Policy Administration System"
          bodyClassName="p-0"
          actions={<Button size="sm" variant="secondary" leftIcon={<FaUserPlus className="h-3 w-3" />} onClick={() => pushToast('info', 'Invite sent', 'Invitation flow is stubbed in this demo.')}>Invite user</Button>}
        >
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: 'var(--color-text-secondary)' }}>
                {['User', 'Role', 'Email', 'Status'].map((h) => (
                  <th key={h} className="px-5 py-2.5 text-left text-xs font-semibold uppercase tracking-wide" style={{ borderBottom: '1px solid var(--color-border-light)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--color-border-light)' }}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold" style={{ backgroundColor: 'var(--color-primary-100)', color: 'var(--color-primary-800)' }}>{u.initials}</span>
                      <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{u.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3" style={{ color: 'var(--color-text-primary)' }}>{u.role}</td>
                  <td className="px-5 py-3" style={{ color: 'var(--color-text-secondary)' }}>{u.email}</td>
                  <td className="px-5 py-3"><Badge variant="success" size="sm">Active</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </SectionCard>
      )}

      {tab === 'distribution' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SectionCard title="Brokers" description={`${brokers.length} appointed producers`} bodyClassName="p-0">
            <ul>
              {brokers.map((b) => (
                <li key={b.id} className="border-b px-5 py-3 last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                  <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{b.agency}</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{b.name} · {b.email} · Lic. {b.licenseNo}</p>
                </li>
              ))}
            </ul>
          </SectionCard>
          <SectionCard title="Carriers" description={`${carriers.length} risk-bearing carriers`} bodyClassName="p-0">
            <ul>
              {carriers.map((c) => (
                <li key={c.id} className="flex items-center justify-between border-b px-5 py-3 last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>{c.name}</p>
                    <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>NAIC {c.naicCode}</p>
                  </div>
                  <Badge variant="info" size="sm">{c.amBestRating}</Badge>
                </li>
              ))}
            </ul>
          </SectionCard>
        </div>
      )}

      {tab === 'configuration' && (
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <SectionCard title="Workflow rules">
            <div className="divide-y" style={{ borderColor: 'var(--color-border-light)' }}>
              <Toggle checked={rules.requireBinder} onChange={(v) => setRules((r) => ({ ...r, requireBinder: v }))} label="Require binder before issuance" description="Block policy issuance until a binder is generated." />
              <Toggle checked={rules.autoAssign} onChange={(v) => setRules((r) => ({ ...r, autoAssign: v }))} label="Auto-assign underwriters" description="Route new submissions to the least-loaded underwriter." />
              <Toggle checked={rules.dualApproval} onChange={(v) => setRules((r) => ({ ...r, dualApproval: v }))} label="Dual approval over $500k" description="Require a senior underwriter co-sign on large premiums." />
              <Toggle checked={rules.notifyBroker} onChange={(v) => setRules((r) => ({ ...r, notifyBroker: v }))} label="Notify broker on status change" description="Email the producing broker when a policy changes status." />
            </div>
          </SectionCard>
          <div className="space-y-6">
            <SectionCard title="Products" description="Product lines available for new policies" bodyClassName="p-0">
              <ul>
                {products.map((p) => (
                  <li key={p} className="flex items-center justify-between border-b px-5 py-3 last:border-b-0" style={{ borderColor: 'var(--color-border-light)' }}>
                    <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>{p}</span>
                    <button type="button" role="switch" aria-checked={activeProducts[p]} aria-label={p} onClick={() => setActiveProducts((s) => ({ ...s, [p]: !s[p] }))} className="relative h-6 w-11 flex-shrink-0 rounded-full transition-colors" style={{ backgroundColor: activeProducts[p] ? 'var(--color-primary-500)' : 'var(--color-border-medium)' }}>
                      <span className={cn('absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform', activeProducts[p] ? 'translate-x-[22px]' : 'translate-x-0.5')} />
                    </button>
                  </li>
                ))}
              </ul>
            </SectionCard>
            <Card>
              <div className="flex items-center gap-3">
                <FaCheckCircle style={{ color: 'var(--color-success-500)' }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>Parento PAS · Demo build</p>
                  <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>Front-end demo · mock data · v0.1.0 · no backend required</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};
